/* A2 — Servidores Evolution (Painel Administrativo).
 *
 * O pool de servidores Evolution que o dono gerencia — invisível para o
 * cliente. Ligada ao endpoint real `/api/servers` (Fase 0): listar, adicionar e
 * remover (bloqueado se houver empresa usando). A chave da Evolution é
 * write-only. Nebula. */

import React, { useState } from 'react';

import { api } from '../api/cliente';
import { useRecurso } from '../estado/recurso';
import { relativo } from './formato';
import { Aviso, Button, card, Cabecalho, Icon, IconButton, Nota, Tag } from './ui';

export default function Servidores() {
  const recurso = useRecurso((sinal) => api.servidores(sinal), []);
  const servidores = recurso.dados?.itens || [];
  const [adicionando, setAdicionando] = useState(false);
  const [removendo, setRemovendo] = useState(null);
  const [erro, setErro] = useState(null);

  async function remover(s) {
    setRemovendo(s.server_id);
    setErro(null);
    try {
      await api.removerServidor(s.server_id);
      recurso.recarregar();
    } catch (e) {
      setErro(e.codigo === 'servidor_em_uso' ? `"${s.apelido}" tem empresas usando — reatribua antes de remover.` : (e.mensagem || 'Não foi possível remover.'));
    } finally {
      setRemovendo(null);
    }
  }

  return (
    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      <Cabecalho
        titulo="Servidores"
        descricao="O pool de servidores Evolution que você gerencia. Invisível para o cliente."
        acoes={<Button variant="primary" icon="plus" onClick={() => setAdicionando(true)}>Adicionar servidor</Button>}
      />

      {adicionando && <AdicionarServidor aoFechar={() => setAdicionando(false)} aoCriado={() => recurso.recarregar()} />}

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '0 24px 8px' }}>
        {erro && <div style={{ margin: '0 0 14px', padding: '10px 14px', background: 'var(--danger-soft)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-control)', font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', color: 'var(--danger-text)' }}>{erro}</div>}

        {recurso.erro ? (
          <section style={card}><Aviso titulo="Os servidores não carregaram" texto={recurso.erro.mensagem} acao={recurso.erro.temporario ? 'Tentar de novo' : null} aoAcao={recurso.recarregar} /></section>
        ) : !recurso.carregando && servidores.length === 0 ? (
          <section style={card}><Aviso titulo="Nenhum servidor" texto="Adicione o primeiro servidor para conectar números." acao="Adicionar servidor" aoAcao={() => setAdicionando(true)} /></section>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 16 }}>
              {servidores.map((s) => (
                <div key={s.server_id} style={{ ...card, padding: 18, gap: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 'var(--radius-md)', background: 'var(--surface-raised)', color: 'var(--text-brand)' }}>
                        <Icon name="server" size={18} />
                      </span>
                      <span style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ font: 'var(--type-title)' }}>{s.apelido}</span>
                        <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{s.evoapi_url}</span>
                      </span>
                    </span>
                    <Tag tone={s.ativo ? 'success' : 'neutral'} dot={s.ativo}>{s.ativo ? 'Ativo' : 'Inativo'}</Tag>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                    <span style={{ display: 'flex', flexDirection: 'column' }}>
                      <span className="nb-tab" style={{ font: 'var(--fw-bold) 22px/1.1 var(--font-display)' }}>{s.empresas}</span>
                      <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)' }}>{s.empresas === 1 ? 'empresa' : 'empresas'}</span>
                    </span>
                    <span style={{ display: 'flex', flexDirection: 'column' }}>
                      <span className="nb-tab" style={{ font: 'var(--fw-bold) 22px/1.1 var(--font-display)' }}>{s.numeros}</span>
                      <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)' }}>{s.numeros === 1 ? 'número' : 'números'}</span>
                    </span>
                    <span style={{ marginLeft: 'auto' }}>
                      <Tag tone={s.tem_chave ? 'success' : 'warning'}>{s.tem_chave ? 'Chave configurada' : 'Sem chave'}</Tag>
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
                    <span style={{ font: 'var(--type-caption)', color: 'var(--text-placeholder)' }}>criado {relativo(s.criado_em)}</span>
                    <Button
                      variant="ghost"
                      icon="circle-slash"
                      onClick={() => remover(s)}
                      disabled={s.empresas > 0 || removendo === s.server_id}
                      style={{ height: 32, padding: '0 10px', fontSize: 'var(--fs-label)', color: s.empresas > 0 ? 'var(--text-placeholder)' : 'var(--danger-text)' }}
                      title={s.empresas > 0 ? 'Há empresas usando este servidor' : 'Remover servidor'}
                    >
                      {removendo === s.server_id ? '…' : 'Remover'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <Nota>A associação considera o servidor principal da empresa e também os servidores usados por seus números.</Nota>
          </>
        )}
      </div>
    </div>
  );
}

const rot = { font: 'var(--type-label)', color: 'var(--text-secondary)' };
const inp = { height: 40, padding: '0 12px', width: '100%', background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-control)', color: 'var(--text-primary)', font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', outline: 'none' };

function AdicionarServidor({ aoFechar, aoCriado }) {
  const [apelido, setApelido] = useState('');
  const [url, setUrl] = useState('');
  const [chave, setChave] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState(null);
  const valido = apelido.trim() && url.trim();

  async function enviar() {
    setEnviando(true);
    setErro(null);
    try {
      await api.criarServidor({ apelido: apelido.trim(), evoapi_url: url.trim(), evoapi_apikey: chave.trim() || undefined });
      aoCriado?.();
      aoFechar();
    } catch (e) {
      setErro(e.codigo === 'ja_cadastrado' ? 'Já existe um servidor com esta URL.' : (e.mensagem || 'Não foi possível adicionar.'));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div style={{ position: 'absolute', inset: 0, background: 'rgba(4,5,20,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, zIndex: 6 }}>
      <div style={{ ...card, width: 460, maxWidth: '100%', boxShadow: 'var(--shadow-md)' }}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)' }}>
          <h3 style={{ margin: 0, font: 'var(--type-h3)' }}>Adicionar servidor</h3>
          <IconButton icon="x" label="Fechar" onClick={aoFechar} />
        </header>
        <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={rot}>Apelido</span>
            <input style={inp} value={apelido} onChange={(e) => setApelido(e.target.value)} placeholder="evo-sp-02" />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={rot}>URL da Evolution</span>
            <input style={inp} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://evo.exemplo.com.br" />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={rot}>Chave da API (write-only)</span>
            <input type="password" style={inp} value={chave} onChange={(e) => setChave(e.target.value)} placeholder="Cole a chave (opcional agora)" autoComplete="new-password" />
          </label>
          {erro && <span style={{ font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', color: 'var(--danger-text)' }}>{erro}</span>}
        </div>
        <footer style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 20px', borderTop: '1px solid var(--border-subtle)', background: 'var(--surface-sunken)' }}>
          <Button variant="ghost" onClick={aoFechar}>Cancelar</Button>
          <Button variant="primary" onClick={enviar} disabled={!valido || enviando} style={!valido || enviando ? { opacity: 0.6, cursor: 'default' } : undefined}>{enviando ? 'Adicionando…' : 'Adicionar'}</Button>
        </footer>
      </div>
    </div>
  );
}
